import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Mail, Lock, Eye, EyeOff, Loader2 } from 'lucide-react';
import BrandLogo from '../components/BrandLogo';

const Login = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { loginWithEmail } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast.error('Por favor completa todos los campos');
      return;
    }

    setLoading(true);
    
    const result = await loginWithEmail(email, password);
    
    setLoading(false);
    
    if (result.success) {
      toast.success(`Bienvenido, ${result.user.name || result.user.email}`);
      
      // Clear any pending MeLi site
      localStorage.removeItem('pendingMeliSite');
      
      // If we were bounced here from an expired session (apiClient's 401
      // interceptor), send the user back where they were. Only accept a
      // same-site path to avoid an open redirect via the query param.
      const redirectTo = searchParams.get('redirect');
      const isSafeRedirect = redirectTo && redirectTo.startsWith('/') && !redirectTo.startsWith('//');

      const isPaymentFailed = ['past_due', 'unpaid'].includes(result.subscriptionStatus);

      // Redirect based on role, subscription health, and MeLi connection status
      if (result.user.role === 'super_admin') {
        navigate('/admin');
      } else if (result.user.role === 'consultant') {
        navigate('/afiliados', { replace: true });
      } else if (result.restricted && isPaymentFailed) {
        navigate('/update-payment', { replace: true });
      } else if (result.restricted) {
        toast.error('Tu suscripción no está activa. Elige un plan para continuar.');
        navigate('/select-plan', { replace: true });
      } else if (isSafeRedirect) {
        navigate(redirectTo);
      } else if (result.user.meli_connected) {
        navigate('/dashboard');
      } else {
        navigate('/connect-meli');
      }
    } else {
      toast.error(result.error);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo/Header */}
        <div className="text-center mb-8">
          <BrandLogo className="h-8 mx-auto mb-3" />
          <p className="text-muted-foreground">Ingresa a tu cuenta</p>
        </div>

        {/* Login Form */}
        <div className="bg-card/60 backdrop-blur-xl border border-border rounded-2xl p-8">
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Email */}
            <div className="space-y-2">
              <label className="text-sm text-muted-foreground">Correo electrónico</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground/70" />
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="tu@correo.com"
                  className="pl-10 bg-muted border-border text-foreground placeholder:text-muted-foreground/70"
                  disabled={loading}
                />
              </div>
            </div>

            {/* Password */}
            <div className="space-y-2">
              <label className="text-sm text-muted-foreground">Contraseña</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground/70" />
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Tu contraseña"
                  className="pl-10 pr-10 bg-muted border-border text-foreground placeholder:text-muted-foreground/70"
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground/70 hover:text-muted-foreground"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Forgot Password */}
            <div className="text-right">
              <Link 
                to="/forgot-password" 
                className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
              >
                ¿Olvidaste tu contraseña?
              </Link>
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 hover:bg-blue-500 text-white h-11"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Ingresando...
                </>
              ) : (
                'Ingresar'
              )}
            </Button>
          </form>

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-card/60 text-muted-foreground/70">o</span>
            </div>
          </div>

          {/* Register Link */}
          <p className="text-center text-muted-foreground text-sm">
            ¿No tienes cuenta?{' '}
            <Link to="/register" className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300">
              Regístrate
            </Link>
          </p>
        </div>

        {/* Back to Landing */}
        <p className="text-center mt-6">
          <Link to="/" className="text-muted-foreground/70 hover:text-muted-foreground text-sm">
            ← Volver al inicio
          </Link>
        </p>
      </div>
    </div>
  );
};

export default Login;
